export function formatDateIsoString(isoString: string): string {
  const date = new Date(isoString);
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDateWithSlash(date: Date): string {
  let day = date.getDate();
  let month = date.getMonth() + 1;
  let year = date.getFullYear();

  let dayStr = day < 10 ? '0' + day : day.toString();
  let monthStr = month < 10 ? '0' + month : month.toString();

  return monthStr + '/' + dayStr + '/' + year;
}

export function formatTime(isoString: string): string {
  const date = new Date(isoString);
  let hours = date.getHours();
  let minutes = date.getMinutes();
  let seconds = date.getSeconds();
  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12;
  hours = hours ? hours : 12;
  const strTime = `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${
    seconds < 10 ? '0' : ''
  }${seconds} ${ampm} (EST)`;

  return strTime;
}

export function convertDateFormat(inputDate: string) {
  const [year, month, day] = inputDate.split('-');
  const formattedMonth = parseInt(month, 10);
  const formattedDay = parseInt(day, 10);
  return `${formattedMonth}/${formattedDay}/${year}`;
}

export function getAMorPM(time: string) {
  const [hour, minutes] = time.split(':');
  const hourIn24Format = parseInt(hour, 10);

  if (hourIn24Format < 12) {
    return 'AM';
  }
  return 'PM';
}

export function convertTo12HrFormat(time24: string) {
  const [hours, minutes] = time24.split(':');
  let newHours = parseInt(hours, 10);
  let period = 'AM';

  if (newHours >= 12) {
    period = 'PM';
    if (newHours > 12) {
      newHours -= 12;
    }
  } else if (newHours === 0) {
    newHours = 12;
  }

  const formattedHours = newHours < 10 ? `0${newHours}` : newHours;
  return `${formattedHours}:${minutes} ${period}`;
}

export function convertToHourMinuteFormat(time: string): string {
  const timeParts = time.split(':');
  if (timeParts.length < 2) {
    return 'Invalid format';
  }
  const hour = timeParts[0];
  if (hour.includes('.')) {
    const dayParts = time.split('.');
    if (dayParts.length === 2) {
      return `${dayParts[0]}d`;
    }
  }
  const minute = timeParts[1];
  return `${hour}h ${minute}m`;
}

export function getNowDate(fullYear: boolean = false): string {
  const now = new Date();
  if (fullYear) {
    return `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`;
  }
  return `${now.getMonth() + 1}/${now.getDate()}/${now
    .getFullYear()
    .toString()
    .substr(-2)}`;
}

export function getNowTime(): string {
  const now = new Date();

  const hours = now.getHours() > 12 ? now.getHours() - 12 : now.getHours();
  const minutes = now.getMinutes() < 10 ? `0${now.getMinutes()}` : now.getMinutes();
  const ampm = now.getHours() >= 12 ? 'pm' : 'am';
  return `${hours}:${minutes} ${ampm} EST`;
}

export function convertToUSDateFormatWithTime12H(isoDate: string) {
  const date = new Date(isoDate);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');

  let period = 'AM';
  if (hours >= 12) {
    period = 'PM';
  }
  if (hours > 12) {
    hours -= 12;
  } else if (hours === 0) {
    hours = 12;
  }

  return `${month}/${day}/${year} ${hours}:${minutes} ${period}`;
}

export function convertMinutesToHHmm(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const minutesLeft = minutes % 60;
  return `${hours}:${minutesLeft.toString().padStart(2, '0')}:00`;
}

/**
 * Converts a 4-digit time number (e.g., 1437) to 12-hour format with AM/PM (e.g., "2:37 PM")
 * @param timeNumber 4-digit time number in 24-hour format (HHMM)
 * @returns formatted time string in 12-hour format with AM/PM
 */
export function convertTimeStringTo12Hour(timeNumber: number): string {
  if (timeNumber < 0 || timeNumber > 2359) {
    return 'Invalid time format';
  }

  const timeString = timeNumber.toString().padStart(4, '0');
  const hours24 = parseInt(timeString.substring(0, 2), 10);
  const minutes = parseInt(timeString.substring(2, 4), 10);

  if (minutes > 59) {
    return 'Invalid time format';
  }

  let hours12 = hours24;
  const period = hours24 >= 12 ? 'PM' : 'AM';

  if (hours24 === 0) {
    hours12 = 12;
  } else if (hours24 > 12) {
    hours12 = hours24 - 12;
  }

  const formattedMinutes = minutes.toString().padStart(2, '0');
  return `${hours12}:${formattedMinutes} ${period}`;
}

export function convertSecondsToHHmmss(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${hours}:${minutes}:${remainingSeconds}`;
}

export function convertFromDatePickerToDatabaseFormat(dateString: string) {
  const parts = dateString.split('/');
  const year = parts[2];
  const month = parts[0].padStart(2, '0');
  const day = parts[1].padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getDateForEndpoint(daysAgo = 0) {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${month}/${day}/${year}`;
}

export function getCurrentUTCDateTime() {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  const hours = String(now.getUTCHours()).padStart(2, '0');
  const minutes = String(now.getUTCMinutes()).padStart(2, '0');
  const seconds = String(now.getUTCSeconds()).padStart(2, '0');
  const milliseconds = String(now.getUTCMilliseconds()).padStart(3, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
}

export function formatOnlyDate(dateString: string): string {
  const date = new Date(dateString);

  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return `${month}/${day}/${year}`;
}

export function extractDate(dateTimeStr: string): string {
  const dateOnly = dateTimeStr.split(' ')[0];
  return dateOnly;
}

/**
 * Formats a UTC date string to "5/26/2023 at 9:43 AM" format in local timezone
 * @param dateString UTC date string to format
 * @returns formatted date string in local timezone
 */
export function formatDateWithAt(dateString: string): string {
  // Create date from UTC string
  const date = new Date(dateString);

  // Convert UTC to local time
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  // Get local date components
  const month = localDate.getMonth() + 1;
  const day = localDate.getDate();
  const year = localDate.getFullYear();

  // Get local time components
  let hours = localDate.getHours();
  const minutes = localDate.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';

  // Convert to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${month}/${day}/${year} at ${hours}:${formattedMinutes} ${ampm}`;
}

export function convertFromDatabaseToDatePickerFormat(dateString: string) {
  const [year, month, day] = dateString.split('-');
  return `${month}/${day}/${year}`;
}

/**
 * Formats a date string from ISO format (YYYY-MM-DDT00:00:00+00:00) to M/D/YYYY
 * without using Date object (string manipulation only)
 * @param dateString ISO date string to format
 * @returns formatted date string in M/D/YYYY format
 */
export function formatDateString(dateString: string): string {
  if (!dateString) return '';

  // Extract only the date part (YYYY-MM-DD)
  const datePart = dateString.split('T')[0];

  // Split year, month and day
  const [year, month, day] = datePart.split('-');

  // Return in M/D/YYYY format (without leading zeros)
  return `${parseInt(month)}/${parseInt(day)}/${year}`;
}

/**
 * Formats a date string to the local timezone with a specific format
 * @param dateString UTC date string to format
 * @param format Optional format string (defaults to "h:mm a")
 * @returns formatted date string in local timezone
 */
export function formatToLocalTimezone(
  dateString: string,
  format: string = 'h:mm a'
): string {
  if (!dateString) return '';

  // Create date from UTC string
  const date = new Date(dateString);

  // Get local timezone offset in minutes
  const offsetMinutes = date.getTimezoneOffset();

  // Convert UTC to local time by adjusting for timezone offset
  const localDate = new Date(date.getTime() - offsetMinutes * 60000);

  // Format based on requested format
  if (format === 'h:mm a') {
    // Format time in 12-hour format with AM/PM
    let hours = localDate.getHours();
    const minutes = localDate.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    return `${hours}:${formattedMinutes} ${ampm}`;
  } else if (format === 'MMM d, y') {
    // Format date as "May 26, 2023"
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = months[localDate.getMonth()];
    const day = localDate.getDate();
    const year = localDate.getFullYear();

    return `${month} ${day}, ${year}`;
  }

  // Default to a simple date string if format not recognized
  return localDate.toLocaleString();
}
